import {Models as FeatureRequestsModels} from "../../../src/featurerequests/models";
import {Models as FeatureTogglesModels} from "../../../src/featuretoggles/models";
import {Models as ClientsModels} from "../../../src/clients/models";

export const backendHost = APP_ENV === "development" ? "http://localhost:3000" : "";
const endpoint = `${backendHost}/api/v1`;

// For debug purposes
const delay = <T>(func: () => Promise<T>): Promise<T> => {
    if (APP_ENV === "development")
        return new Promise(resolve => setTimeout(() => resolve(func() as T), 1000))
    return func()
}

const devHeaders: RequestInit = APP_ENV === "production" ? {} : {
    credentials: "include",
    mode: "cors",
};

const get = (url: string) => {
    return delay(() => fetch(url, devHeaders));
}

const post = (url: string, body: any = undefined) => delay(() => fetch(url, {
        method: "POST",
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        ...devHeaders
    })
)

const put = (url: string, body: any = "") => delay(() => fetch(url, {
        method: "PUT",
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body),
        ...devHeaders
    })
)

const remove = (url: string) => delay(() => fetch(url, {
        method: "DELETE",
        ...devHeaders
    })
)

export const api = {
    server: {
        version: () => get(`${endpoint}/version`)
    },
    feedback: {
        features: {
            me: () => get(`${endpoint}/feedback/features/me`),
            register: (user: string) => get(`${endpoint}/feedback/features/register?user=${user}`),
            all: () => get(`${endpoint}/feedback/features`),
            single: (item: { id: number }) => get(`${endpoint}/feedback/features/${item.id}`),
            create: (item: FeatureRequestsModels.Item) => post(`${endpoint}/feedback/features`, item),
            update: (item: FeatureRequestsModels.Item) => put(`${endpoint}/feedback/features/${item.id}`, item),
            delete: (item: FeatureRequestsModels.Item) => remove(`${endpoint}/feedback/features/${item.id}`),

            vote: (vote: FeatureRequestsModels.Vote) => post(`${endpoint}/feedback/features/votes`, vote),
            unvote: (item: { id: number }, user: string) =>
                remove(`${endpoint}/feedback/features/votes?itemId=${item.id}&user=${user}`),

            comments: {
                create: (comment: FeatureRequestsModels.Comment) => post(`${endpoint}/feedback/features/comments`, comment),
                update: (comment: FeatureRequestsModels.Comment) => put(`${endpoint}/feedback/features/comments/${comment.id}`, comment),
                delete: (comment: FeatureRequestsModels.Comment) => remove(`${endpoint}/feedback/features/comments/${comment.id}`),
            }
        }
    },
    settings: {
        all: () => get(`${endpoint}/data/settings`),
    },
    featuretoggles: {
        all: () => get(`${endpoint}/features/all`),
        create: (obj: FeatureTogglesModels.FeatureToggleTarget) => post(`${endpoint}/features`, obj),
        update: (obj: FeatureTogglesModels.FeatureToggleTarget) => put(`${endpoint}/features/${obj.id}`, obj),
        delete: (obj: FeatureTogglesModels.FeatureToggleTarget) => remove(`${endpoint}/features/${obj.id}`),
    },
    clients: {
        all: (filtersOrOffset: ClientsModels.ClientFilter | number = 0, limit = 50) => {
            const params = new URLSearchParams();
            if (typeof filtersOrOffset === "number") {
                params.append("offset", String(filtersOrOffset));
                params.append("limit", String(limit));
            } else {
                params.append("offset", String(filtersOrOffset.offset ?? 0));
                params.append("limit", String(filtersOrOffset.limit ?? limit));
                if (filtersOrOffset.clientID?.trim()) {
                    params.append("clientID", filtersOrOffset.clientID.trim());
                }
                if (filtersOrOffset.appVersion?.trim()) {
                    params.append("appVersion", filtersOrOffset.appVersion.trim());
                }
                if (filtersOrOffset.os?.trim()) {
                    params.append("os", filtersOrOffset.os.trim());
                }
                if (filtersOrOffset.country?.trim()) {
                    params.append("country", filtersOrOffset.country.trim());
                }
                if (filtersOrOffset.maxOpenedTimes !== undefined && !isNaN(filtersOrOffset.maxOpenedTimes)) {
                    params.append("maxOpenedTimes", String(filtersOrOffset.maxOpenedTimes));
                }
            }
            return get(`${endpoint}/clients?${params.toString()}`);
        },
        create: (obj: ClientsModels.Client) => post(`${endpoint}/clients`, obj),
        update: (obj: ClientsModels.Client) => put(`${endpoint}/clients/${obj.id}`, obj),
        delete: (obj: ClientsModels.Client) => remove(`${endpoint}/clients/${obj.id}`),
        markAsRobot: (obj: {
            clientID: string
        }, isRobot: boolean) => post(`${endpoint}/clients/${obj.clientID}/robot?isRobot=${isRobot}`),
        geolocation: (obj: {ip: string}) => get(`${endpoint}/clients/geolocation/${obj.ip}`),

        labels: {
            all: () => get(`${endpoint}/clients/labels`),
            single: (obj: { clientID: string }) => get(`${endpoint}/clients/labels/${obj.clientID}`),
            create: (obj: ClientsModels.ClientLabel) => post(`${endpoint}/clients/labels`, obj),
            update: (obj: ClientsModels.ClientLabel) => put(`${endpoint}/clients/labels/${obj.id}`, obj),
            delete: (obj: ClientsModels.ClientLabel) => remove(`${endpoint}/clients/labels/${obj.id}`),
        }
    }
}
